import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem('gci_chat_session');
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem('gci_chat_session', id);
  }
  return id;
};

const LABELS = {
  fr: {
    title: 'Assistant GCI',
    subtitle: 'Posez vos questions sur vos projets',
    placeholder: 'Écrivez votre message…',
    welcome: "Bonjour ! Je suis l'assistant virtuel de GCI. Comment puis-je vous aider avec votre projet de construction à Kinshasa ?",
    error: "Désolé, une erreur est survenue. Veuillez réessayer.",
  },
  en: {
    title: 'GCI Assistant',
    subtitle: 'Ask about your projects',
    placeholder: 'Type your message…',
    welcome: "Hello! I'm GCI's virtual assistant. How can I help you with your construction project in Kinshasa?",
    error: 'Sorry, an error occurred. Please try again.',
  },
};

const ChatWidget = () => {
  const { language } = useLanguage();
  const t = LABELS[language] || LABELS.fr;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (open && !historyLoaded) {
      fetch(`${API}/api/chat/history/${sessionId.current}`)
        .then((r) => r.json())
        .then((items) => {
          if (Array.isArray(items) && items.length > 0) {
            setMessages(items.map((m) => ({ role: m.role, content: m.content })));
          }
          setHistoryLoaded(true);
        })
        .catch(() => setHistoryLoaded(true));
    }
  }, [open, historyLoaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setLoading(true);
    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: text, language }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let gotError = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                role: 'assistant',
                content: next[next.length - 1].content + data.delta,
              };
              return next;
            });
          } else if (data.error) {
            gotError = true;
          }
        }
      }
      if (gotError) {
        setMessages((prev) => {
          const next = [...prev];
          if (!next[next.length - 1].content) {
            next[next.length - 1] = { role: 'assistant', content: t.error };
          }
          return next;
        });
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: t.error };
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [input, loading, language, t.error]);

  return (
    <div className="fixed bottom-6 right-6 z-[1000]" data-testid="chat-widget">
      {open && (
        <div
          className="mb-4 w-[360px] max-w-[calc(100vw-3rem)] h-[500px] max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="chat-panel"
        >
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#702963' }}>
            <div>
              <p className="text-white font-bold text-sm">{t.title}</p>
              <p className="text-white/70 text-xs">{t.subtitle}</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="chat-close-button"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" data-testid="chat-messages">
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm px-3 py-2 text-sm bg-white border border-gray-200 text-gray-800">
                {t.welcome}
              </div>
            </div>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'text-white rounded-br-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                  }`}
                  style={m.role === 'user' ? { background: '#FF8C00' } : undefined}
                >
                  {m.content || (
                    <Loader2 size={16} className="animate-spin text-gray-400" />
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-gray-200 bg-white flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder={t.placeholder}
              className="flex-1 text-sm px-3 py-2 rounded-full border border-gray-300 focus:outline-none focus:border-[#FF8C00]"
              data-testid="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-50 transition-opacity"
              style={{ background: '#FF8C00' }}
              data-testid="chat-send-button"
              aria-label="Send"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="ml-auto flex w-14 h-14 rounded-full shadow-xl items-center justify-center text-white hover:scale-105 transition-transform"
        style={{ background: open ? '#702963' : '#FF8C00' }}
        data-testid="chat-toggle-button"
        aria-label="Open chat"
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </button>
    </div>
  );
};

export default ChatWidget;
